import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Cell, 
    PieChart, Pie
} from 'recharts';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

// Mock Data for Charts (Static for now, but stats are dynamic)
const priceTrends = [
    { month: 'Jan', price: 4200000 },
    { month: 'Feb', price: 4350000 },
    { month: 'Mar', price: 4300000 },
    { month: 'Apr', price: 4500000 },
    { month: 'May', price: 4800000 },
    { month: 'Jun', price: 4750000 },
    { month: 'Jul', price: 5120000 },
];

const activityData = [
    { month: 'Apr', saved: 12, tours: 1, inquiries: 4 },
    { month: 'May', saved: 18, tours: 3, inquiries: 6 },
    { month: 'Jun', saved: 15, tours: 2, inquiries: 2 },
    { month: 'Jul', saved: 22, tours: 5, inquiries: 8 },
];

const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

export default function BuyerDashboard() {
    const { token } = useAuth();
    const [stats, setStats] = useState({
        wishlistCount: 0,
        bookingCount: 0,
        leadCount: 0,
        preferenceData: [] as { name: string, value: number }[]
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            if (!token) return;
            try {
                const data = await api.getBuyerStats(token);
                if (data) {
                    setStats({
                        wishlistCount: data.wishlistCount || 0,
                        bookingCount: data.bookingCount || 0,
                        leadCount: data.leadCount || 0,
                        preferenceData: data.preferenceData || []
                    });
                }
            } catch (err) {
                console.error('Error fetching buyer stats:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [token]);

    return (
        <UserDashboardLayout>
            {/* Upper Section */}
            <div className="flex flex-col md:flex-row items-center justify-between mb-10">
                <div>
                    <h2 className="text-3xl font-black text-slate-900 mb-2">Workspace Overview</h2>
                    <p className="text-slate-500 font-medium tracking-tight">Track market movements and your personalized activity in real-time.</p>
                </div>
                <div className="mt-6 md:mt-0 flex gap-4">
                    <button className="bg-slate-900 text-white px-6 py-3 rounded-2xl font-bold flex items-center gap-2 hover:bg-black transition-all shadow-lg shadow-slate-200">
                        <i className="fa-solid fa-download"></i> Report
                    </button>
                    <Link to="/project-list" className="bg-blue-600 text-white px-8 py-3 rounded-2xl font-bold hover:shadow-xl transition-all shadow-blue-200 shadow-lg flex items-center gap-2">
                        <i className="fa-solid fa-rocket"></i> Find Now
                    </Link>
                </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 transition-all hover:border-blue-500/20 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                            <i className="fa-regular fa-heart text-xl"></i>
                        </div>
                        <span className="text-emerald-500 text-[10px] font-black bg-emerald-50 px-2 py-1 rounded-full">Real-time</span>
                    </div>
                    <h3 className="text-3xl font-black text-slate-800 mb-1">{loading ? '...' : stats.wishlistCount}</h3>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Saved Inventory</p>
                </div>

                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 transition-all hover:border-indigo-500/20 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                            <i className="fa-regular fa-calendar-check text-xl"></i>
                        </div>
                        <span className="text-blue-500 text-[10px] font-black bg-blue-50 px-2 py-1 rounded-full">Meetings</span>
                    </div>
                    <h3 className="text-3xl font-black text-slate-800 mb-1">{loading ? '...' : stats.bookingCount}</h3>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tours Scheduled</p>
                </div>

                <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 transition-all hover:border-emerald-500/20 group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                            <i className="fa-regular fa-comment-dots text-xl"></i>
                        </div>
                        <span className="text-amber-500 text-[10px] font-black bg-amber-50 px-2 py-1 rounded-full">Inquiries</span>
                    </div>
                    <h3 className="text-3xl font-black text-slate-800 mb-1">{loading ? '...' : stats.leadCount}</h3>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Conversations</p>
                </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
                {/* Area Chart: Price Trends */}
                <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100">
                    <div className="flex items-center justify-between mb-10 pb-4 border-b border-slate-50">
                        <div>
                            <h4 className="text-xl font-bold text-slate-800">Price Appreciation</h4>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Market Index Trends</p>
                        </div>
                        <div className="text-right">
                            <span className="text-lg font-black text-emerald-500">₹5.1M</span>
                            <p className="text-[10px] font-bold text-slate-300">Current Average</p>
                        </div>
                    </div>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <AreaChart data={priceTrends}>
                                <defs>
                                    <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontWeight: 700, fontSize: 10}} dy={10} />
                                <YAxis hide />
                                <Tooltip 
                                    contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'}}
                                    itemStyle={{ fontWeight: 800, color: '#1e293b'}}
                                    labelStyle={{ display: 'none'}}
                                    formatter={(value: any) => [`₹${(value/1000000).toFixed(1)}M`, 'Price Index']}
                                />
                                <Area type="monotone" dataKey="price" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorPrice)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Bar Chart: Activity */}
                <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100">
                    <div className="flex items-center justify-between mb-10 pb-4 border-b border-slate-50">
                        <div>
                            <h4 className="text-xl font-bold text-slate-800">Engagement Logs</h4>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Interactions Per Month</p>
                        </div>
                        <div className="flex gap-2">
                            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-500"></div><span className="text-[8px] font-bold text-slate-400 uppercase">Save</span></div>
                            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-blue-100"></div><span className="text-[8px] font-bold text-slate-400 uppercase">Book</span></div>
                        </div>
                    </div>
                    <div style={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer>
                            <BarChart data={activityData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontWeight: 700, fontSize: 10}} dy={10} />
                                <YAxis hide />
                                <Tooltip 
                                    cursor={{fill: '#f8fafc'}}
                                    contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'}}
                                />
                                <Bar dataKey="saved" fill="#3b82f6" radius={[6, 6, 0, 0]} barSize={20} />
                                <Bar dataKey="inquiries" fill="#6366f1" radius={[6, 6, 0, 0]} barSize={20} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
                {/* Pie Chart: Preferences */}
                <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100 flex flex-col items-center">
                    <h4 className="text-xl font-bold text-slate-800 self-start mb-2">Category Mix</h4>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest self-start mb-8 pb-4 border-b border-slate-50 w-full">Property Interest Profile</p>
                    <div style={{ width: '100%', height: 260 }}>
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={stats.preferenceData.length > 0 ? stats.preferenceData : [{ name: 'No Data', value: 100 }]}
                                    innerRadius={70}
                                    outerRadius={90}
                                    paddingAngle={8}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {stats.preferenceData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                    {stats.preferenceData.length === 0 && <Cell fill="#f1f5f9" />}
                                </Pie>
                                <Tooltip 
                                   contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'}}
                                   itemStyle={{ fontWeight: 800, fontSize: 12}}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="grid grid-cols-2 gap-4 w-full mt-4">
                        {stats.preferenceData.map((p, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }}></div>
                                <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">{p.name} ({p.value}%)</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Portfolio Summary Card */}
                <div className="bg-slate-900 rounded-[2rem] p-10 relative overflow-hidden flex flex-col justify-center">
                    <div className="relative z-10">
                        <h4 className="text-3xl font-black text-white mb-4">Investment Readiness</h4>
                        <p className="text-slate-400 font-medium mb-8 max-w-md">Your profile is currently {stats.wishlistCount > 5 ? '80%' : '30%'} complete based on your active search patterns and saves.</p>
                        <Link to="/my-profile" className="bg-blue-600 text-white px-10 py-4 rounded-2xl font-black shadow-2xl shadow-blue-900/40 hover:scale-105 transition-transform inline-block">
                            Complete Profile
                        </Link>
                    </div>
                    {/* Decorative Elements */}
                    <div className="absolute top-[-20%] right-[-10%] w-64 h-64 bg-blue-600/10 rounded-full blur-3xl"></div>
                    <i className="fa-solid fa-chart-line absolute bottom-[-10px] right-[20px] text-[180px] text-white/5 opacity-5"></i>
                </div>
            </div>
        </UserDashboardLayout>
    );
}
